// Session-start rules for LogRocket. `init` starts recording and the SDK has no
// teardown, so a session we don't want can only be avoided by deciding *before*
// init — never by stopping one that has already begun.

// School-year pages carry the year in their title rather than their slug, so
// the exclusion is matched against the title.
const EXCLUDED_TITLE_MARKERS = ['2025-26', '2025-2026', '2026-27', '2026-2027'];

export const isRecordableTitle = (title: string): boolean =>
  !EXCLUDED_TITLE_MARKERS.some((marker) => title.includes(marker));

// How long the title has to hold still before we trust it. Some routes title
// themselves twice (Schedule shows "Untitled schedule" until its data lands),
// so the first mutation isn't necessarily the page's real title.
const TITLE_QUIET_MS = 500;

// Recording starts only once the decision lands, so this caps how much of a
// session we give up waiting on routes that set no title at all.
const TITLE_DEADLINE_MS = 4000;

// index.html ships a static <title> and analytics initializes from App's mount
// effect, so document.title is still "Agenda Hero" at that point — the titles
// we filter on are set by usePageTitle only after the experience or schedule
// query resolves. Wait for <title> to change and go quiet before reading it.
const whenTitleSettles = (): Promise<string> => {
  const titleElement = document.querySelector('title');

  if (!titleElement) {
    return Promise.resolve(document.title);
  }

  return new Promise((resolve) => {
    let quietTimer: ReturnType<typeof setTimeout> | undefined;

    const settle = () => {
      clearTimeout(quietTimer);
      clearTimeout(deadlineTimer);
      observer.disconnect();
      resolve(document.title);
    };

    const observer = new MutationObserver(() => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(settle, TITLE_QUIET_MS);
    });
    const deadlineTimer = setTimeout(settle, TITLE_DEADLINE_MS);

    observer.observe(titleElement, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  });
};

// Set to false to temporarily record sessions that start on school-year pages.
export const EXCLUDE_SCHOOL_YEAR_PAGES = true;

let decision: Promise<boolean> | null = null;

/**
 * Whether this session may be recorded, resolved once against the page the
 * session started on. Memoized: every caller shares the one decision.
 */
export const shouldRecordSession = (): Promise<boolean> => {
  decision ??= EXCLUDE_SCHOOL_YEAR_PAGES
    ? whenTitleSettles().then(isRecordableTitle)
    : Promise.resolve(true);

  return decision;
};

// The iframes we build from a srcDoc string boot their own LogRocket instance so
// the app's `mergeIframes` recording can stitch them in. They can't decide for
// themselves whether to: an iframe renders before usePageTitle has set the title
// the decision above reads, so the only answer available to it synchronously is
// the bootstrapped "Agenda Hero" — which reads as recordable on exactly the
// school-year pages we mean to exclude. An iframe that inits on its own doesn't
// join a session, it *creates* one, and a created session costs quota whether or
// not anything worth watching ends up in it (AH-2495).
//
// So they wait to be told instead. This fires once the app has actually called
// LogRocket.init, which is a fact rather than a guess, and one that also covers
// the SDK failing to load and the analyticsDisabled escape hatch: no init in the
// app, no instance in the iframe.
let hasStarted = false;
const startListeners = new Set<() => void>();

export const notifySessionRecordingStarted = (): void => {
  hasStarted = true;
  startListeners.forEach((listener) => listener());
  startListeners.clear();
};

/**
 * Calls `listener` once the app has started recording, or right away if it
 * already has. Returns an unsubscribe for listeners that go away first.
 */
export const onSessionRecordingStarted = (
  listener: () => void
): (() => void) => {
  if (hasStarted) {
    listener();
    return () => undefined;
  }

  startListeners.add(listener);

  return () => {
    startListeners.delete(listener);
  };
};
